'use client'

import React, { useEffect, useRef, useState, PropsWithChildren } from 'react'
import { Client, IMessage, ActivationState } from '@stomp/stompjs'
import Cookies from 'js-cookie'
import { WebSocketContext } from './web-socket-context'
import { useMeStore } from '@/store'
import { ensureFreshToken } from '@/lib/axios'
import config from '@/config'

const WebSocketProvider = ({ children }: PropsWithChildren) => {
  const clientRef = useRef<Client | null>(null)
  const subscriptionsRef = useRef<Map<string, any>>(new Map())
  const { meState } = useMeStore()
  const userId = meState.response?.id

  const [userNotifications, setUserNotifications] = useState<any[]>([])
  const [publicNotifications, setPublicNotifications] = useState<any[]>([])
  const [chats, setChats] = useState<any[]>([])
  const [userStatuses, setUserStatuses] = useState<Record<string, boolean>>({})
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    // Don't connect if no token or if we're on the server
    const initialToken = Cookies.get('accessToken')
    if (!initialToken || typeof window === 'undefined') {
      if (process.env.NODE_ENV !== 'production') {
        console.log('[WebSocket] No token provided or server-side, skipping connection')
      }
      return
    }

    let isMounted = true
    const subscriptions = subscriptionsRef.current

    const initializeWebSocket = async () => {
      // Dynamically import SockJS to avoid server-side issues
      const SockJS = (await import('sockjs-client')).default

      if (!isMounted) {
        return
      }

      if (process.env.NODE_ENV !== 'production') {
        console.log('[WebSocket] Initializing connection...')
      }

      const client = new Client({
        webSocketFactory: () => {
          const sockjs = new SockJS(`${config.API_BASE_URL}/ws`, null, {
            transports: ['websocket'],
            timeout: 10000
          })
          return sockjs as any
        },
        connectHeaders: {
          Authorization: `Bearer ${initialToken}`
        },
        beforeConnect: async () => {
          // Ensure we have a fresh (non-expired) token before each (re)connect attempt
          const freshToken = await ensureFreshToken()
          if (freshToken) {
            client.connectHeaders = { Authorization: `Bearer ${freshToken}` }
          } else {
            // No valid token — stop reconnecting, forceLogout will handle the redirect
            client.deactivate()
          }
        },
        debug: (str: string) => {
          if (process.env.NODE_ENV !== 'production') {
            console.log('[STOMP]', str)
          }
        },
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000
      })

      client.onConnect = () => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('✅ WebSocket connected')
        }
        setIsConnected(true)

        subscriptions.clear()

        try {
          const publicSub = client.subscribe('/topic/notifications', (msg: IMessage) => {
            try {
              const data = JSON.parse(msg.body)
              setPublicNotifications(prev => [...prev, data])
            } catch (error) {
              console.error('[WebSocket] Error parsing public notification:', error)
            }
          })
          subscriptions.set('/topic/notifications', publicSub)
        } catch (error) {
          console.error('[WebSocket] Error subscribing to public notifications:', error)
        }

        try {
          const userStatusSub = client.subscribe('/topic/user-status', (msg: IMessage) => {
            try {
              const data = JSON.parse(msg.body)
              const { userId: statusUserId, online } = data
              setUserStatuses(prev => ({
                ...prev,
                [statusUserId]: online
              }))
            } catch (error) {
              console.error('[WebSocket] Error parsing user status:', error)
            }
          })
          subscriptions.set('/topic/user-status', userStatusSub)
        } catch (error) {
          console.error('[WebSocket] Error subscribing to user status:', error)
        }

        if (userId) {
          try {
            const userNotifSub = client.subscribe(`/user/queue/notifications`, (msg: IMessage) => {
              try {
                const data = JSON.parse(msg.body)
                setUserNotifications(prev => [...prev, data])
              } catch (error) {
                console.error('[WebSocket] Error parsing user notification:', error)
              }
            })
            subscriptions.set(`/user/queue/notifications`, userNotifSub)

            const chatSub = client.subscribe(`/user/queue/chat`, (msg: IMessage) => {
              try {
                const data = JSON.parse(msg.body)
                setChats(prev => [...prev, data])
              } catch (error) {
                console.error('[WebSocket] Error parsing chat message:', error)
              }
            })
            subscriptions.set(`/user/queue/chat`, chatSub)
          } catch (error) {
            console.error('[WebSocket] Error subscribing to user-specific topics:', error)
          }
        }
      }

      client.onStompError = frame => {
        console.error('❌ WebSocket STOMP error:', frame.headers['message'])
        if (process.env.NODE_ENV !== 'production') {
          console.error('[WebSocket] Details:', frame.body)
        }
        setIsConnected(false)
      }

      client.onWebSocketError = event => {
        console.error('❌ WebSocket error:', event)
        setIsConnected(false)
      }

      client.onDisconnect = () => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('🔌 WebSocket disconnected')
        }
        setIsConnected(false)
        subscriptions.clear()
      }

      client.onWebSocketClose = event => {
        if (event.code !== 1000 && process.env.NODE_ENV !== 'production') {
          console.log(`🔌 WebSocket closed: ${event.code} - ${event.reason || 'Unknown reason'}`)
        }
        setIsConnected(false)
      }

      try {
        client.activate()
        clientRef.current = client
      } catch (error) {
        console.error('[WebSocket] Activation error:', error)
      }
    }

    void initializeWebSocket()

    return () => {
      isMounted = false

      if (process.env.NODE_ENV !== 'production') {
        console.log('[WebSocket] Cleaning up connection...')
      }

      if (clientRef.current?.state === ActivationState.ACTIVE) {
        subscriptions.forEach(subscription => {
          try {
            subscription.unsubscribe()
          } catch (error) {
            console.error('[WebSocket] Error unsubscribing:', error)
          }
        })
        subscriptions.clear()
      }

      if (clientRef.current) {
        void clientRef.current.deactivate()
      }
    }
  }, [userId])

  const subscribe = (destination: string, onMessage: (msg: any) => void) => {
    if (!clientRef.current || clientRef.current.state !== ActivationState.ACTIVE) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[WebSocket] Cannot subscribe - client not connected')
      }
      return
    }

    try {
      const subscription = clientRef.current.subscribe(destination, (msg: IMessage) => {
        try {
          onMessage(JSON.parse(msg.body))
        } catch (error) {
          console.error('[WebSocket] Error parsing message:', error)
        }
      })
      subscriptionsRef.current.set(destination, subscription)

      if (process.env.NODE_ENV !== 'production') {
        console.log('[WebSocket] Subscribed to:', destination)
      }
    } catch (error) {
      console.error('[WebSocket] Subscribe error:', error)
    }
  }

  const sendMessage = (destination: string, body: any) => {
    if (!clientRef.current || clientRef.current.state !== ActivationState.ACTIVE) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[WebSocket] Cannot send message - client not connected')
      }
      return
    }

    try {
      clientRef.current.publish({
        destination,
        body: JSON.stringify(body)
      })

      if (process.env.NODE_ENV !== 'production') {
        console.log('[WebSocket] Message sent to:', destination)
      }
    } catch (error) {
      console.error('[WebSocket] Send message error:', error)
    }
  }

  return (
    <WebSocketContext.Provider
      value={{ userNotifications, publicNotifications, chats, userStatuses, isConnected, subscribe, sendMessage }}>
      {children}
    </WebSocketContext.Provider>
  )
}

export default WebSocketProvider
